import { rem } from 'polished'
import React, { Fragment, useContext } from 'react'
import styled from 'styled-components'

import Clickable from 'components/Common/Clickable'
import SlideDown from 'components/Common/SlideDown'
import IconButton from 'components/Luxkit/Button/IconButton'
import DropdownChevron from 'components/Luxkit/Dropdown/DropdownChevron'
import Label from 'components/Luxkit/Label'
import Heading from 'components/Luxkit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { verticalChildSpacing } from 'components/utils/spacing'
import { HeaderHeightContext } from 'contexts/HeaderStateContext'
import useToggle from 'hooks/useToggle'
import zIndex from 'styles/tools/z-index'
import Notes from 'tripPlanner/components/CuratedTrip/Notes'
import ItemCard from 'tripPlanner/components/ItemCard/ItemCard'
import { CuratedTripSection } from 'tripPlanner/types/common'

const Container = styled.div`
  position: relative;
  z-index: 0;
`

const SectionHeader = styled(Clickable)`
  width: 100%;
  position: sticky;
  top: 0;
  z-index: ${zIndex.min};
  background-color: ${(props) => props.theme.palette.neutral.default.one};
  display: flex;
  align-items: top;
  justify-content: space-between;
  border-top: 1px solid ${(props) => props.theme.palette.neutral.default.three};
  padding: ${rem(22)} ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(22)} ${rem(40)};
  }
`

const HeaderContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${verticalChildSpacing[16]}
`

const SectionContent = styled.div`
  padding: ${rem(20)} ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} ${rem(40)};
  }

  > * + * {
    margin-top: ${rem(20)};
  }
`

interface Props {
  section: CuratedTripSection
}

function Section({ section }: Props) {
  const { sectionHeader, items } = section

  const { value: open, toggle } = useToggle(true)

  const {
    heights: { tripPlanner: tpHeaderHeight },
  } = useContext(HeaderHeightContext)

  const sectionId = sectionHeader?.id

  return (
    <Container id={sectionId}>
      {sectionHeader && (
        <SectionHeader style={{ top: tpHeaderHeight }} onClick={toggle}>
          <HeaderContent>
            {sectionHeader.startDay && (
              <Label kind="tertiary" variant="dark">
                {`Day ${sectionHeader.startDay}`}
              </Label>
            )}
            <Heading variant="heading4" colour="neutral-eight">
              {sectionHeader.title}
            </Heading>
          </HeaderContent>
          <IconButton kind="tertiary" variant="ghost" nonInteractable>
            <DropdownChevron direction={open ? 'up' : 'down'} />
          </IconButton>
        </SectionHeader>
      )}
      <SlideDown show={open}>
        <SectionContent>
          {sectionHeader?.notes && <Notes content={sectionHeader.notes} />}

          {items.map((item) => (
            <Fragment key={item.id}>
              {item.type !== 'NOTE' && (
                <ItemCard item={item} variant="tripSummary" />
              )}
              {item.notes && <Notes content={item.notes} />}
            </Fragment>
          ))}
        </SectionContent>
      </SlideDown>
    </Container>
  )
}

export default Section
